import moment from 'moment';
import _ from 'underscore';
import logger from 'logger/logHelper';
// https://github.com/GA-MO/react-confirm-alert
import { confirmAlert } from './common/confirm-alert/confirm-alert';
import {
  BILLING_TYPE,
  ITEM_TITLE_TYPES,
  ITEM_TYPES,
  PROFILES
} from './constants';
import {
  checkIfArrayNotEmpty,
  checkIfDictionaryNotEmpty,
  checkValue
} from 'utils/validator';
import { uploadFile } from 'utils/fileUploadUtils';
import dexieService from './services/offlineServices/dexie.service';
import { isQBOSettingEnabled } from 'common/pricebook/utility';
import i18n from './i18n/config';
const { t } = i18n;
// import { sub } from "date-fns";

const getJobTime = (startTime, endTime) => {
  const start = moment(startTime);
  const end = moment(endTime);

  if (moment(start).format('MM/DD/YYYY') === moment(end).format('MM/DD/YYYY')) {
    const date = moment(start).format('DD MMM YYYY');
    const sTime = moment(start).format('hh:mm A');
    const eTime = moment(end).format('hh:mm A');
    return `${date} - ${sTime} - ${eTime}`;
  }
  const sDate = moment(start).format('DD MMM YYYY');
  const eDate = moment(end).format('DD MMM YYYY');
  const sTime = moment(start).format('hh:mm A');
  const eTime = moment(end).format('hh:mm A');
  return `${sDate} - ${sTime} - ${eDate} - ${eTime}`;
};

const convertSecondsToHourMinute = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  let minutes = Math.floor((seconds % 3600) / 60);
  // Round the minute value
  if (seconds % 60 >= 30) {
    minutes++;
  }
  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}`;
};

const generateUniqueId = () => {
  return Math.random().toString(36).substring(7);
};

const formatPhoneNumber = (phoneNumberString) => {
  const cleaned = `${phoneNumberString}`.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return null;
};

const getMobileOperatingSystem = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  if (/windows phone/i.test(userAgent)) {
    return 'Windows Phone';
  }

  if (/android/i.test(userAgent)) {
    return 'Android';
  }

  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'iOS';
  }

  return null;
};

const getDefaultTnc = async ({
  tncList = [],
  isInvoice,
  customerAddressMetadata = {}
}) => {
  let defaultTnC;
  if (!(typeof isInvoice === 'boolean')) {
    logger.logError(
      `Error while fetching default terms: Expected boolean, found ${isInvoice}`
    );
    return;
  }
  const activeTncList = tncList.filter((tnc) => tnc.isActive);
  if (checkIfDictionaryNotEmpty(customerAddressMetadata)) {
    if (
      isInvoice &&
      checkIfDictionaryNotEmpty(
        customerAddressMetadata.invoiceTermsAndCondition
      )
    ) {
      defaultTnC = customerAddressMetadata.invoiceTermsAndCondition;
    } else if (
      !isInvoice &&
      checkIfDictionaryNotEmpty(
        customerAddressMetadata.estimateTermsAndCondition
      )
    ) {
      defaultTnC = customerAddressMetadata.estimateTermsAndCondition;
    }
    if (checkIfDictionaryNotEmpty(defaultTnC)) {
      let isActiveTnc = false;
      activeTncList.forEach((tnc) => {
        if (tnc.id === defaultTnC.id) {
          isActiveTnc = true;
          return;
        }
      });
      if (!isActiveTnc) {
        defaultTnC = {};
      }
    }
  }

  if (!checkIfDictionaryNotEmpty(defaultTnC)) {
    activeTncList.forEach((tnc) => {
      // if isInvoice give priority to invoiceDefault tnc else give priority to estimateDefault tnc
      if (
        (isInvoice && tnc.isInvoiceDefault) ||
        (!isInvoice && tnc.isEstimateDefault)
      ) {
        defaultTnC = tnc;
        return;
      }
      if (tnc.isDefault && !defaultTnC) {
        // if no separate tnc found assign default tnc
        defaultTnC = tnc;
      }
    });
  }
  return defaultTnC || {};
};

const deleteAllCookies = () => {
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
};

const parseErrorResponse = (error) => {
  logger.logError(error);
  const currentUser = getCurrentUser();
  if (!currentUser) {
    logger.logError(`no user found :${JSON.stringify(error)}`);
  }
  switch (error && error.response && error.response.status) {
    case 401:
      // 'TOKEN_EXPIRED';
      deleteAllCookies();
      localStorage.clear();
      window.location.href = '/login';
      break;
    case 404:
      // 'NOT_FOUND'
      break;
    case 500:
      logger.logError(error);
      break;
    default:
      logger.logInfo('default');
  }
};
window.onerror = parseErrorResponse;
/**
 * @description - This function will return the company settings from local storage
 * @param {*} key
 */
const getFromCompanySettings = (key) => {
  if (!key) return null;
  let data = localStorage.getItem('companySettings');
  if (!data) return null;
  data = JSON.parse(data);
  if (!data) return null;

  return data[key];
};

/**
 * @description This function will return the id of an object or string for a collection
 * @param {string|object} object
 */
const getModelIdFromObject = (object) => {
  if (!object) return undefined;
  if (typeof object === 'number') {
    return `${object}`;
  }
  if (typeof object === 'string') {
    if (object.split('$')[1]) {
      return object.split('$')[1];
    }
    return object.split('$')[0];
  }
  if (typeof object === 'object') {
    return object.id || object._id || object.objectId;
  }
  return undefined;
};

/**
 * @description Using this for assigning temporary id to offline created data.
 * @returns returns a 13 digit long number
 */
function getObjectId() {
  const id = Math.random() * Math.pow(10, 13);
  return Math.round(id);
}
/**
 * @description This function will remove all the keys form an object that do not have a value.
 * @param {object} object
 */
const compact = (object) => {
  Object.keys(object).forEach((key) => {
    if (object[key] === null || object[key] === undefined) {
      delete object[key];
    }
  });
  return object;
};

/**
 * @author pravin-tiwari
 * @desc This function will return the customer unique Id based as per QuickBooks want
 * @function custBuilder
 * @param {string} fName
 * @param {string} lName
 * @param {string} stopUniqueNumber
 * @return {string} 'lName, fName (UniqueID)'
 */
function custUniqueIdBuilder(fName, lName, stopUniqueNumber) {
  let addressInfoRandomNumber = '';

  if (!stopUniqueNumber) {
    addressInfoRandomNumber = `(${randomString(7, '0123456789')})`; // Get 7 digit randome number and enclose this in bracket.
  }
  let value = '';
  value =
    fName && lName
      ? `${lName}, ${fName}`
      : fName && !lName
      ? fName
      : !fName && lName
      ? lName
      : '';
  return value && addressInfoRandomNumber
    ? `${value} ${addressInfoRandomNumber}`
    : value;
}

/**
 * @author pravin-tiwari
 * @desc This function will return the customer unique Id based as per QuickBooks want
 * @function custBuilderCompany
 * @param {string} fName
 * @param {string} lName
 * @param {string} companyName
 * @return {string} 'Company Name (UniqueID)'
 */
function custUniqueIdBuilderCompany(
  fName,
  lName,
  companyName,
  stopUniqueNumber
) {
  let addressInfoRandomNumber = '';

  if (!stopUniqueNumber) {
    addressInfoRandomNumber = `(${randomString(7, '0123456789')})`; // Get 7 digit randome number and enclose this in bracket.
  }
  let value = '';
  if (companyName) {
    if (addressInfoRandomNumber) {
      value = `${companyName} ${addressInfoRandomNumber}`;
    } else {
      value = companyName;
    }
    return value;
  }
  return custUniqueIdBuilder(fName, lName, stopUniqueNumber);
}

// This function will return the random string of length u provide as parameter.
// @param length, Number
// @param charSet, String
function randomString(len, charSet) {
  charSet =
    charSet || 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let rndStr = '';
  for (let i = 0; i < len; i++) {
    const randomPoz = Math.floor(Math.random() * charSet.length);
    rndStr += charSet.substring(randomPoz, randomPoz + 1);
  }
  return rndStr;
}

/**
 * @author pravin-tiwari
 * @desc This function will return the service unique Id based as per QuickBooks parent child relationship
 * @function servUniqueIdBuilder
 * @param {string} fName
 * @param {string} lName
 * @param {number} servLength
 * @param {ParseObject} billingObject
 * @param {ParseObject} stopUniqueNumber
 * @return {string} 'lName, fName (UniqueId)'
 */
function servUniqueIdBuilder(fName, lName, stopUniqueNumber) {
  // var addressInfoRandomNumber = '';

  let addressInfoRandomNumber = '';

  if (!stopUniqueNumber) {
    addressInfoRandomNumber = `(${randomString(7, '0123456789')})`; // Get 7 digit randome number and enclose this in bracket.
  }
  let value = '';
  value =
    fName && lName
      ? `${lName}, ${fName}`
      : fName && !lName
      ? fName
      : !fName && lName
      ? lName
      : '';
  return value && addressInfoRandomNumber
    ? `${value} ${addressInfoRandomNumber}`
    : value;
}

/**
 * @author pravin-tiwari
 * @desc This function will return the customer unique Id based as per QuickBooks want
 * @function servUniqueIdBuilderAddressLine1
 * @param {string} fName
 * @param {string} lName
 * @param {string} companyName
 * @param {number} billAdd1
 * @param {boolean} stopUniqueNumber
 * @return {string} 'Service Address Line 1' // but then then 31 character, if more will get trim
 */
function custUniqueIdBuilderAddressLine1(
  fName,
  lName,
  companyName,
  billAdd1,
  stopUniqueNumber
) {
  let addressInfoRandomNumber = '';
  if (!stopUniqueNumber) {
    addressInfoRandomNumber = `(${randomString(7, '0123456789')})`; // Get 7 digit randome number and enclose this in bracket.
  }
  let value = '';
  if (billAdd1) {
    if (addressInfoRandomNumber) {
      value = `${billAdd1} ${addressInfoRandomNumber}`;
    } else {
      value = billAdd1;
    }
    return value;
  }
  return custUniqueIdBuilder(fName, lName, stopUniqueNumber);
}

/**
 * @desc This function return the specific type of parent unique id
 * @name getCustomerChildIDByPattern
 * @param {string} pattern
 * @param {string} fName
 * @param {string} lName
 * @param {string} addressLine1
 * @param {string} servLength
 * @param {string} billingObject
 * @param {string} stopUniqueNumber
 * @return {string} generatedUniqueId
 */
function getCustomerChildIDByPattern(
  pattern,
  fName,
  lName,
  compnayName,
  billAdd1,
  stopUniqueNumber
) {
  let returnValue = '';
  if (pattern) {
    switch (pattern) {
      case '0': // First Name, Last Name (uniqueID)
        returnValue = custUniqueIdBuilder(lName, fName, stopUniqueNumber); // To geth first name nad last name pattern just pass a reverse params
        break;
      case '1': // Last Name, First Name (uniqueID)
        returnValue = custUniqueIdBuilder(fName, lName, stopUniqueNumber);
        break;
      case '2': // Company Name (uniqueID)
        returnValue = custUniqueIdBuilderCompany(
          fName,
          lName,
          compnayName,
          stopUniqueNumber
        );
        break;
      case '3': // Address Line 1 (uniqueID)
        returnValue = custUniqueIdBuilderAddressLine1(
          fName,
          lName,
          compnayName,
          billAdd1,
          stopUniqueNumber
        );
        break;
      default:
        // Last Name, First Name (uniqueID)
        returnValue = custUniqueIdBuilder(fName, lName, stopUniqueNumber);
        break;
    }
  } else {
    returnValue = custUniqueIdBuilder(fName, lName, stopUniqueNumber);
  }
  // If setting is based on first name and last name but still blacnk we will genrate by company name
  if (!returnValue) {
    returnValue = custUniqueIdBuilderCompany(
      fName,
      lName,
      compnayName,
      stopUniqueNumber
    );
  }
  return returnValue;
}

/**
 * @desc This function return the specific type of parent unique id
 * @name getCustomerParentIDByPattern
 * @param {string} pattern
 * @param {string} fName
 * @param {string} lName
 * @param {string} compnayName
 * @return {string} generatedUniqueId
 */
function getCustomerParentIDByPattern(
  pattern,
  fName,
  lName,
  compnayName,
  billAdd1,
  stopUniqueNumber
) {
  let returnValue = '';
  if (pattern) {
    switch (pattern) {
      case '0': // First Name, Last Name (uniqueID)
        returnValue = custUniqueIdBuilder(lName, fName, stopUniqueNumber); // To geth first name nad last name pattern just pass a reverse params
        break;
      case '1': // Last Name, First Name (uniqueID)
        returnValue = custUniqueIdBuilder(fName, lName, stopUniqueNumber);
        break;
      case '2': // Company Name (uniqueID)
        returnValue = custUniqueIdBuilderCompany(
          fName,
          lName,
          compnayName,
          stopUniqueNumber
        );
        break;
      case '3': // Address Line 1 (uniqueID)
        returnValue = custUniqueIdBuilderAddressLine1(
          fName,
          lName,
          compnayName,
          billAdd1,
          stopUniqueNumber
        );
        break;
      default:
        // Last Name, First Name (uniqueID)
        returnValue = custUniqueIdBuilder(fName, lName, stopUniqueNumber);
        break;
    }
  } else {
    returnValue = custUniqueIdBuilder(fName, lName, stopUniqueNumber);
  }
  // If setting is based on first name and last name but still blacnk we will genrate by company name
  if (!returnValue) {
    returnValue = custUniqueIdBuilderCompany(
      fName,
      lName,
      compnayName,
      stopUniqueNumber
    );
  }
  return returnValue;
}

/**
 * @description - This function will check if the given string is a valid url or not
 * @param {string} str
 */
const isValidURL = (str) => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i'
  ); // fragment locator
  return !!pattern.test(str);
};

const getBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const isBase64 = (str) => {
  if (!str || typeof str !== 'string') {
    return false;
  }
  // Check if it contains a Base64 data URL prefix and extract the actual Base64 part
  const base64PrefixPattern = /^data:[\w+/]+;base64,/;
  if (base64PrefixPattern.test(str)) {
    str = str.replace(base64PrefixPattern, ''); // Remove the prefix
  }
  // Validate the remaining string as Base64
  const base64Regex = /^(?:[A-Za-z0-9+/]{4})*?(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
  return base64Regex.test(str);
};

const isBlobObjectURL = (url) => {
  return typeof url === 'string' && url.startsWith('blob:');
};

// Capitalize some string
function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const NoData = (props) => {
  return (
    <div className='list-blank'>
      <div className='list-blank-area' style={{ background: '#c7c7c730' }}>
        <p>
          <i className='file outline icon' />
        </p>
        <p className='header'>{props.title}</p>
        <p>{props.text}</p>
      </div>
    </div>
  );
};

function validateEmail(email) {
  const re =
    /^(([^<>()\\.,;:\s@"]+(\.[^<>()\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

function validateMobile(num) {
  const re = /^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/;
  return re.test(num);
}

/**
 * Sort array of objects based on object key
 * @param	{Array<Object>}			list		Array of objects
 * @param	{string}				key			Object key to get sort value
 * @param	{string}				order		Sort order 'ASC' | 'DSC'
 *
 * @return	{Array<Object>}			Sorted object array
 */
const stringSortObjectArray = (list, key, order) => {
  function getStringValue(obj) {
    return (obj && obj[key] ? obj[key] : '').toUpperCase();
  }

  function sortAscending(a, b) {
    const valueA = getStringValue(a);
    const valueB = getStringValue(b);
    if (valueA < valueB) return -1;
    if (valueA > valueB) return 1;
    return 0;
  }

  function sortDescending(a, b) {
    const valueA = getStringValue(a);
    const valueB = getStringValue(b);
    if (valueA > valueB) return -1;
    if (valueA < valueB) return 1;
    return 0;
  }
  if (Array.isArray(list) && list.length > 0) {
    return order === 'DSC'
      ? list.sort(sortDescending)
      : list.sort(sortAscending);
  }
  return list;
};

/**
 * Get customer name from service address object
 * @param	{<Object>}		addressObj		Service address object
 *
 * @return	{String}		Customer name
 */
const getCustomerNameFromServiceAddress = (addressObj = {}) => {
  // if ('name' in addressObj) {
  //   return addressObj.name
  //     ? addressObj.name
  //     : `${addressObj.firstname} ${addressObj.lastname}`;
  // }
  // return `${addressObj.firstname} ${addressObj.lastname}`;
  let customerName = '';
  const {
    name = '',
    firstname = '',
    lastname = '',
    displayName = ''
  } = addressObj;
  if (!name) {
    customerName = `${firstname} ${lastname}`;
  } else {
    customerName = name;
  }
  customerName = customerName.trim();
  if (!customerName && displayName) {
    customerName = displayName;
  }
  customerName = customerName.trim();
  return customerName;
};

/**
 * Get customer address from service address object
 * @param	{<Object>}		addressObj		Service address object
 *
 * @return	{String}		Full customer address
 */
const getCustomerAddressFromServiceAddress = (addressObj = {}) => {
  let mergedAddress = '';
  mergedAddress += addressObj.addressLine1 || '';
  mergedAddress += addressObj.addressLine2
    ? `, ${addressObj.addressLine2}`
    : '';
  mergedAddress += addressObj.city ? `, ${addressObj.city} ` : '';
  mergedAddress += addressObj.state ? `${addressObj.state} ` : ' ';
  mergedAddress += addressObj.country ? `${addressObj.country} ` : ' ';
  mergedAddress += addressObj.zipCode
    ? `${addressObj.zipCode} `
    : addressObj.zipcode
    ? `${addressObj.zipcode} `
    : '';
  return mergedAddress;
};

/**
 * Get user full name, Unassigned if no details passed
 * @param {Object} techObj - User object with firstname, lastname
 * @returns String of user's full name
 */
const getUserFullName = (techObj = {}) => {
  if (_.isEmpty(techObj)) {
    return `Unassigned`;
  }
  const { firstname = '', lastname = '' } = techObj;
  return `${firstname} ${lastname}`.trim();
};

/**
 * @description - This function will tell you to create or stop to create a invoice based on single invoice restriction.
 * @param {*} invoices - Array of invoice which will hold the invoice type and isVoid flag
 */
const isSingleInvoiceRestricted = (invoices) => {
  const invoiceSettings = getFromCompanySettings('invoiceSettings') || {};
  const isSingleInvoiceOn = invoiceSettings.singleInvoiceRestrict || false;
  invoices = (invoices || []).filter((invoice) => {
    if (Object.keys(invoice || {}).length) {
      return !invoice.isVoid && invoice.invoiceType === BILLING_TYPE.INVOICE;
    }
    return [];
  });
  return !!(isSingleInvoiceOn && invoices.length);
};

/**
 * Round numbers to n value
 * @param {*} n
 * @param {*} digits
 */
const roundTo = (n, digits) => {
  if (digits === undefined) {
    digits = 0;
  }

  const multiplicator = Math.pow(10, digits);
  n = parseFloat((n * multiplicator).toFixed(11));
  return Math.round(n) / multiplicator;
};

const getDecimalVal = (val) => {
  return roundTo(val, 2); // Math.round(val * 100) / 100;
};

window.onoffline = () => {
  logger.logInfo('User is offline');
  confirmAlert({
    customUI: ({ onClose }) => {
      return (
        <div className='confirm-box'>
          <h1>{t('zt_sidebar.offline')}</h1>
          <p>{t('zt_sidebar.working_in_offline_mode')}</p>
          <div>
            <button
              onClick={() => {
                logger.logInfo('User is using application in offline mode');
                onClose();
              }}
              className='cancel'
            >
              {t('button.okay')}
            </button>
          </div>
        </div>
      );
    }
  });
};

const handleOfflineStatus = (changeState) => () => {
  confirmAlert({
    customUI: ({ onClose }) => {
      return (
        <div className='confirm-box'>
          <h1>{t('zt_sidebar.offline')}</h1>
          <p>{t('zt_sidebar.working_in_offline_mode')}</p>
          <div>
            <button
              onClick={() => {
                onClose();
                changeState();
              }}
              className='cancel'
            >
              {t('button.okay')}
            </button>
          </div>
        </div>
      );
    }
  });
};

window.ononline = async () => {
  logger.logInfo('User is back online');
  confirmAlert({
    customUI: ({ onClose }) => {
      return (
        <div className='confirm-box'>
          <h1>{t('label.online')}</h1>
          <p>{t('label.online_text')}</p>
          <div>
            <button onClick={onClose} className='cancel'>
              {t('button.okay')}
            </button>
            <button
              className='success'
              onClick={() => {
                window.location.reload();
                onClose();
              }}
            >
              {t('button.refresh')}
            </button>
          </div>
        </div>
      );
    }
  });
};

const handleOnlineStatus = (changeState) => async () => {
  confirmAlert({
    customUI: ({ onClose }) => {
      return (
        <div className='confirm-box'>
          <h1>{t('label.online')}</h1>
          <p>{t('label.online_text')}</p>
          <div>
            <button
              onClick={() => {
                changeState();
                onClose();
              }}
              className='cancel'
            >
              {t('button.okay')}
            </button>
            <button
              className='success'
              onClick={() => {
                window.location.reload();
                onClose();
              }}
            >
              {t('button.refresh')}
            </button>
          </div>
        </div>
      );
    }
  });
};

const getGroupPrice = (item) => {
  const { items } = item;
  let total = 0;
  (items || []).forEach((item) => {
    total += (item?.sellPrice || 0) * (item?.quantity || 0);
  });
  return total;
};

const getDateFilterLocalStorage = (name) => {
  return localStorage.getItem(name) ? localStorage.getItem(name) : new Date();
};

const setDateFilterLocalStorage = (name, val) => {
  localStorage.setItem(name, val);
};
const getTime = (date) => {
  date = typeof date === 'object' && date.iso ? date.iso : date;
  return new Date(date).getTime();
};
/**
 * @description Returns Default Invoice Item. Called When No Line Items Added.
 */
const getDefaultItem = () => {
  const defaultItem = {};
  defaultItem.name = 'Default';
  defaultItem.discountPercentage = 0;
  defaultItem.quantity = 1;
  defaultItem.rate = 0;
  defaultItem.isApproved = true;
  defaultItem.itemType = ITEM_TYPES.FLATRATE;
  // defaultItem.qbLineItemExisted = false;
  return defaultItem;
};

const getDate = (date) => {
  if (date.constructor === String) {
    return date;
  }
  if (date && date.iso) {
    return date.iso;
  }
  if (!date) {
    logger.logInfo(`invalid date ${new Error().stack}`);
    return new Date();
  }
  return date;
};

const getCurrentUser = () => {
  return JSON.parse(localStorage.getItem('current-user'));
};

const getCurrentUserName = () => {
  const user = getCurrentUser();
  const name = `${user.firstName} ${user.lastName}`;
  return name.trim();
};

const isBillingRestricted = () => {
  const { profile: { name = '' } = {} } = getCurrentUser() || {};
  return name === PROFILES.BILLING_RESTRICTED;
};

const getAccessToken = () => {
  return localStorage.getItem('access-token');
};

const updateAccessTokenInCache = (token) => {
  if (token) {
    localStorage.setItem('access-token', token);
  }
};

const getImageUrlFromImageArray = (imageData = "[ { file: '' } ]") => {
  return JSON.parse(imageData || "[ { file: '' } ]")[0];
};
const prepareItemObjWithImage = (item = {}) => {
  const image = getImageUrlFromImageArray(item.images);
  item.image = image?.file || '';
  return item;
};

const getRefreshToken = () => {
  return localStorage.getItem('refresh-token');
};

const getCompany = () => {
  return JSON.parse(localStorage.getItem('company'));
};

const isUserAdmin = () => {
  const user = getCurrentUser();
  return !!(
    user &&
    user.profile &&
    user.profile.name === PROFILES.ADMINISTRATOR
  );
};

const shouldHideAmount = () => {
  /**
   *  '0', 'Administrator'
   *  '1', 'Dispatcher'
   *  '2', 'Technician'
   *  '3', 'Service Manager'
   *  '4', 'Helper'
   *  '5', 'TechnicianRestricted'
   *  '6', 'ViewRestricted'
   */
  const user = getCurrentUser() || {};
  const { role } = user;
  return role === 6;
};

const fetchGenericData = () => {
  const promises = [
    CompanySettings.fetchStaticData(),
    GeneralAPIs.fetchStaticDataList()
  ];
  return Promise.all(promises);
};

const getFromHistory = (key) => {
  const { history } = window;
  let { state } = history;
  state = state && state.state ? state.state : {};
  return state[key];
};



const getSignatureId = (invoice) => {
  let signatureId = '';
  try {
    logger.logInfo('getSignatureId Called');
    if (invoice.signature) {
      if (typeof invoice.signature === 'string') {
        signatureId = invoice.signature;
      }
      if (invoice.signature.id) {
        signatureId = invoice.signature.id;
      }
    }
    logger.logInfo(`getSignatureId Returned : ${signatureId}`);
  } catch (error) {
    logger.logError(error);
  }
  return signatureId;
};

const getEmailRecipientName = (addressDetails) => {
  return (
    addressDetails?.displayName ||
    addressDetails?.name ||
    `${addressDetails?.firstName || ''} ${addressDetails?.lastName || ''}`
  );
};

const lineItemTypeFromId = (id) => {
  switch (id) {
    case 1:
      return 'Custom';
    case 2:
      return 'FlatRate';
    case 3:
      return 'ServiceAgreement';
    case 4:
      return 'PMAFee';
    default:
      return '';
  }
};

const getAxiosError = (err) => {
  if (err.response) {
    err = err.response;
    err = err.data || {};
    err = err.exception || err;
    return err.error || err;
  }
  return err;
};

const addTimeStamp = (arr, key) => {
  arr = arr || [];
  key = key || 'locallyCreatedOn';
  return arr.map((entry) => {
    entry[key] = new Date();
    return entry;
  });
};

function showFullyQualifiedName(item) {
  const { parentItem, fullyQualifiedName } = item || {};
  let hasParentItem = false;
  if (isQBOSettingEnabled()) {
    hasParentItem = checkIfDictionaryNotEmpty(parentItem);
  } else {
    hasParentItem = checkValue(parentItem);
  }
  return hasParentItem && !!fullyQualifiedName;
}

function getLineItemNameFromTitleType(item = {}) {
  const {
    invoiceSettings: {
      lineItemTitleType = ITEM_TITLE_TYPES.CODE_SECTION_DESCRIPTION
    } = {}
  } = window.companySettings || {};
  let title = `${item?.name || item?.code || ''}`;
  switch (lineItemTitleType) {
    case ITEM_TITLE_TYPES.CODE:
      title = `${item?.code || item?.name || ''}`;
      break;
    case ITEM_TITLE_TYPES.CODE_DESCRIPTION:
      title = `${item?.code || item?.name || ''}${
        item?.description ? ` - ${item.description}` : ''
      }`;
      break;
    case ITEM_TITLE_TYPES.CODE_SECTION:
      title = `${item?.code || item?.name || ''}${
        item?.categoryId?.code
          ? ` - ${item.categoryId.code}`
          : item?.section
          ? ` - ${item.section}`
          : ''
      }`;
      break;
    case ITEM_TITLE_TYPES.CODE_SECTION_DESCRIPTION:
      title = `${item?.code || item?.name || ''}${
        item?.categoryId?.code
          ? ` - ${item.categoryId.code}`
          : item?.section
          ? ` - ${item.section}`
          : ''
      }${item?.description ? ` - ${item.description}` : ''}`;
      break;
  }
  return title;
}

function getImageUrl(attachment) {
  if (attachment.type && attachment.type.indexOf('image') > -1) {
    return attachment.file;
  }
  if (attachment.data && typeof attachment.data === 'string') {
    return attachment.data;
  }
  if (
    attachment.type &&
    (attachment.type.indexOf('x-mpegURL') > -1 ||
      attachment.type.indexOf('video') > -1)
  )
    return '/media/VIDEO.png';
  if (
    attachment.type &&
    (attachment.type.indexOf('PDF') > -1 || attachment.type.indexOf('pdf') > -1)
  )
    return '/media/PDF.png';
  return '/media/FILE.png';
}

function isWorkOrderSignatureMandatory(companySettings) {
  return (companySettings || window?.companySettings)?.ticketSettings
    ?.isWorkOrderSignatureMandatory;
}

/**
 * Checks whether job tags are enabled in company settings.
 * @returns {boolean} True if tags are enabled, false otherwise.
 */
function checkIfTagsAreEnabled() {
  return window.companySettings?.genericSettings?.tagsEnabled ?? false;
}

async function uploadFilesToS3(
  files = [],
  moduleEntityId = '',
  moduleName = ''
) {
  const currentUser = getCurrentUser();
  let attachments = [];
  const attachmentPromises = [];
  const attachmentDetails = [];
  try {
    for (const file of files) {
      const uploadPromise = uploadFile(
        file,
        moduleEntityId,
        moduleName,
        currentUser.companyId
      );
      attachmentPromises.push(uploadPromise);
      attachmentDetails.push({
        altText: file.name,
        type: file.type
      });
    }
    attachments = await Promise.all(attachmentPromises);
    // Needed extra step to preserve legacy code compatibility
    for(let i = 0; i < attachments.length; i++) {
      attachmentDetails[i].file = attachments[i];
    }
    return { attachments: attachmentDetails };
  } catch (error) {
    return { error, attachments };
  }
}

const clearLocalData = async () => {
  try {
    const registration = navigator.serviceWorker.getRegistration();
    const subscription = registration?.pushManager?.getSubscription() || null;
    if (subscription) {
      subscription.unsubscribe();
    }
  } catch (error) {
    //Ignored
  }
  const allCaches = await caches.keys();
  (allCaches || []).forEach(async (entry) => {
    caches.delete(entry);
  });
  dexieService.clearStorage();
  localStorage.clear();
};

/**
 * The function generates a random string of specified length using a pool of characters.
 * @param [length=10] - The `length` parameter is an optional parameter that specifies the length of
 * the random string to be generated. If no value is provided for `length`, it defaults to 10.
 * @returns The function `generateRandomString` returns a randomly generated string of characters.
 */
const generateRandomString = (length = 10) => {
  const characterPool =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ' + 'abcdefghijklmnopqrstuvwxyz' + '0123456789';
  let randomString = '',
    characterPoolLength = characterPool.length;
  for (let index = 0; index < length; index++) {
    const randomNumber = Math.floor(Math.random() * characterPoolLength);
    randomString += characterPool.substring(randomNumber, randomNumber + 1);
  }
  return randomString;
};

const toTitleCase = (str) => {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};
const customDebounce = (func, sleep = 1000) => {
  let timer;
  return function (...args) {
    const context = this;
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      timer = null;
      func.apply(context, args);
    }, sleep);
  };
};

const addAlpha = (color, opacity) => {
  // Coerce values so it is between 0 and 1.
  const _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
  return color + _opacity.toString(16).toUpperCase().padStart(2, '0');
};

const darkenHexColor = (hex, percent) => {
  // Remove '#' if present
  hex = hex.replace('#', '');

  // Convert hex to RGB
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Calculate darker shade
  r = Math.floor(r * (1 - percent / 100));
  g = Math.floor(g * (1 - percent / 100));
  b = Math.floor(b * (1 - percent / 100));

  // Convert back to hex
  const darkerHex = `#${(r < 16 ? '0' : '') + r.toString(16)}${
    (g < 16 ? '0' : '') + g.toString(16)
  }${(b < 16 ? '0' : '') + b.toString(16)}`;
  return darkerHex;
};

const fileTypeUtils = (() => {
  // Define valid image types
  const types = ['jpeg', 'png', 'jpg', 'svg', 'bmp'];

  // Function to normalize the file type to lowercase
  const normalizeType = (type) => type.toLowerCase();

  // Function to check if a file type is an image
  const isImageType = (type) => types.includes(normalizeType(type));

  // Function to check if a file type is a PDF
  const isPdfType = (type) => normalizeType(type) === 'pdf';

  // Function to get the source URL based on the file type
  const getFileSrc = (type, file) => {
    if (isImageType(type)) {
      return file;
    }
    return isPdfType(type) ? '/media/PDF.png' : '/media/FILE.png';
  };

  // Return an object exposing the functions
  return {
    normalizeType,
    isImageType,
    isPdfType,
    getFileSrc
  };
})();

const getTotalFileSize = (files = [], precision = 2) => {
  const totalFileSize = files.reduce((acc, file) => acc + file.size, 0);
  const sizeInMB = (totalFileSize / (1024 * 1024)).toFixed(precision);
  return sizeInMB;
};

const removePropertiesFromObject = (object, properties) => {
  if (checkIfDictionaryNotEmpty(object) && checkIfArrayNotEmpty(properties)) {
    properties.forEach((property) => {
      delete object[property];
    });
  }
};

const formatTimeToHoursMinutes = (time) => {
  const minutes = `0${Math.floor((time / (60 * 1000)) % 60)}`.slice(-2);
  const hours = `0${Math.floor(time / (60 * 60 * 1000))}`.slice(-2);
  return `${hours}h ${minutes}m`;
};

const getPathSegment = (index, defaultValue = '') => {
  const pathSegments = window.location.pathname.split('/');
  return pathSegments[index] || defaultValue;
};

const getTerritorySettings = () => {
  const { genericSettings:
    { territorySettings:
      { isTerritoryEnabled = false, allowedRolesForTerritory = [] } = {}
    } = {}
  } = window.companySettings || {};
  return [isTerritoryEnabled, allowedRolesForTerritory];
};

const isTerritoryFeatureEnabled = () => {
  const [isTerritoryEnabled] = getTerritorySettings();
  return isTerritoryEnabled;
};

export {
  fileTypeUtils,
  customDebounce,
  isWorkOrderSignatureMandatory,
  getGroupPrice,
  showFullyQualifiedName,
  getLineItemNameFromTitleType,
  getCurrentUser,
  isBillingRestricted,
  getAccessToken,
  getRefreshToken,
  getCompany,
  getJobTime,
  convertSecondsToHourMinute,
  generateUniqueId,
  formatPhoneNumber,
  getMobileOperatingSystem,
  parseErrorResponse,
  getFromCompanySettings,
  getModelIdFromObject,
  getBase64,
  isBase64,
  isBlobObjectURL,
  getCustomerChildIDByPattern,
  servUniqueIdBuilder,
  custUniqueIdBuilder,
  getCustomerParentIDByPattern,
  isValidURL,
  capitalize,
  NoData,
  validateEmail,
  validateMobile,
  stringSortObjectArray,
  getCustomerAddressFromServiceAddress,
  getCustomerNameFromServiceAddress,
  isSingleInvoiceRestricted,
  roundTo,
  getDecimalVal,
  getDateFilterLocalStorage,
  setDateFilterLocalStorage,
  getDefaultItem,
  compact,
  getTime,
  getDate,
  getFromHistory,
  isUserAdmin,
  lineItemTypeFromId,
  getAxiosError,
  getCurrentUserName,
  updateAccessTokenInCache,
  addTimeStamp,
  getImageUrl,
  getObjectId,
  handleOfflineStatus,
  handleOnlineStatus,
  shouldHideAmount,
  getUserFullName,
  getDefaultTnc,
  getSignatureId,
  getImageUrlFromImageArray,
  prepareItemObjWithImage,
  uploadFilesToS3,
  getEmailRecipientName,
  clearLocalData,
  generateRandomString,
  toTitleCase,
  addAlpha,
  darkenHexColor,
  getTotalFileSize,
  removePropertiesFromObject,
  formatTimeToHoursMinutes,
  getPathSegment,
  checkIfTagsAreEnabled,
  getTerritorySettings,
  isTerritoryFeatureEnabled,
};
